<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import Badge from '@/components/ui/Badge.vue'
import type { TaskState } from '@/api/tasks'

interface Props {
  status: TaskState
  size?: 'sm' | 'md'
}

const props = withDefaults(defineProps<Props>(), {
  size: 'sm',
})

const { t } = useI18n()

interface BadgeSpec {
  variant: 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'outline'
  label: string
  pulse?: boolean
}

const spec = computed<BadgeSpec>(() => {
  switch (props.status) {
    case 'active':
    case 'downloading':
      return { variant: 'primary', label: t('tasks.state.downloading'), pulse: true }
    case 'paused':
      return { variant: 'warning', label: t('tasks.state.paused') }
    case 'waiting':
    case 'pending':
      return { variant: 'info', label: t('tasks.state.waiting') }
    case 'complete':
      return { variant: 'success', label: t('tasks.state.complete') }
    case 'error':
      return { variant: 'danger', label: t('tasks.state.error') }
    case 'removed':
      return { variant: 'neutral', label: t('tasks.state.removed') }
    default:
      return { variant: 'outline', label: t('tasks.state.unknown') }
  }
})
</script>

<template>
  <Badge :variant="spec.variant" :size="size" :dot="true">
    {{ spec.label }}
  </Badge>
</template>